/**
 * @author Mr_FabiozZz[mr.fabiozzz@gmail.com]
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import sass from "./index.module.sass";
import ManagementSoftware from "./TS/ManagementSoftware";
import InformListVols from "./TS/InformList";
import LocationVols, {ILocation} from "./TS/Location";
import TechListVols from "./TS/TTH";
import ModalPortal from "components-new/ModalPortal";
import CreateRow from "pages/StepPages/StepTwoDotTwo/CreateRow";
import { $api } from "libs/axios";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import cn from "classnames";
import HeadPage from "pages/StepPages/StepFour/HeadPage";
import { Button } from "components/shared";
import { BUTTON_VARIANTS } from "components/shared/Button/Button";
import { ReactComponent as AddIcon } from "images/PlusIcon.svg";
import { useDispatch } from "react-redux";
import { ChangeStatus } from "toolkit/steps/stepTwo/objectsRegister/objectsRegister.slice";
import OperatingSystem, {IData} from "./TS/OperatingSystem";

/**
 * локальный хук, чтобы не плодить дубли на разных страницах решил сделать его в корне родителя,
 * вряд ли его можно будет использовать в других местах, но если получится можно будет переместить и переименовать
 *
 * @param func - функция которая должна выполнится если передана дата
 *
 * @typedef {} ReturnModalHook
 * @property {{active:boolean,data:any}} modal - active - стэйт модалки (открыта/закрыта), data - данные которые записываются при открытии модалки
 * @property {Function} submit - функция при применении изменений в модалке
 * @property {Function} abort - функция для отмены действия в модалки или ее закрытия
 * @property {Function} openModal - функция для открытия модалки
 *
 * @return {ReturnModalHook}
 */
export function useModalStepTwoDotTwo<T>(func: Function) {
	const [modal, setModal] = useState<{ active: boolean; data: T | null }>({ active: false, data: null });

	function submit() {
		if (modal.data) {
			func(modal.data);
			setModal({ active: false, data: null });
		}
	}

	function abort() {
		setModal({ active: false, data: null });
	}

	function openModal(data: T) {
		setModal({ active: true, data });
	}

	return {
		modal,
		submit,
		abort,
		openModal,
	};
}

/**
 * интерфейс таблиц для страницы
 */
export interface IOrganization {
	name: string;
	location: ILocation[];
	TTH: IData[];
	Soft: IData[];
	IL: IData[];
	MS: IData[];
}

/**
 * Запрос к бэку за первоначальными данными
 * возвращает, либо данные, либо false
 */
async function fetchTableData(id: number) {
	try {
		let [location, TTH, IL, Soft, MS] = await Promise.all([
			$api.get(`/object/${id}/locations?part=0&count=99999`).then((r) => r.data),
			$api.get(`/object/${id}/means?part=0&count=99999`).then((r) => r.data),
			$api.get(`/object/${id}/softwares?part=0&count=99999`).then((r) => r.data),
			$api.get(`/object/${id}/os?part=0&count=99999`).then((r) => r.data),
			$api.get(`/object/${id}/tools?part=0&count=99999`).then((r) => r.data),
		]);
		return {
			location,
			TTH,
			IL,
			Soft,
			MS,
		};
	} catch (e) {
		return false;
	}
}

interface ILocationState {
	id: number;
	name: string;
}

interface IDirections {
	direction: "left" | "right";
	exit: boolean;
}
const routing = {
	1: "address",
	2: "influence",
	3: "operating_sys",
	4: "software",
	5: "means",
};
const StepTwoDotTwo = () => {
	const dispatch = useDispatch();
	const location = useLocation();
	const navigate = useNavigate();

	const [direction, setDirection] = useState<IDirections>({ direction: "right", exit: false });

	const isFirstLoad = useRef(true);

	const [activeTab, setActiveTab] = useState(1);
	function changeActiveTab(e: React.MouseEvent<EventTarget>) {
		const target = e.target as HTMLElement;
		const tab: any = target.dataset.tab;
		if (Number(tab) !== activeTab) {
			if (Number(tab) > activeTab) {
				setDirection({ direction: "left", exit: true });
			} else {
				setDirection({ direction: "right", exit: true });
			}
			setTimeout(() => {
				setDirection((prevState) => ({ ...prevState, exit: false }));
				setActiveTab(Number(tab));
				navigate(location.pathname.replace(/\/(.+)$/, routing[tab as keyof typeof routing]), {
					state: location.state,
					replace: true,
				});
			}, 100);
		}
	}

	/**
	 * Состояние и запрос за объектами воздействия
	 */
	const [influenceObjects, setInfluenceObjects] = useState<{id:number,name:string}[]>([]);
	useEffect(() => {
		$api.get(`/directory/dropdown/object/additional/influences`).then((r) => {
			setInfluenceObjects(r.data.map((item: string, i: number) => ({id: i + 1, name: item})));
		});
	}, []);

	useEffect(() => {
		if (isFirstLoad.current) {
			navigate(location.pathname.replace(/\/(.+)$/, routing[1]), {
				state: location.state,
				replace: true,
			});
			isFirstLoad.current = false;
		}
		//eslint-disable-next-line
	}, []);

	const requestForMeans = useCallback(() => {
		if (location.state.id) {
			$api
				.get<{ id: number; name: string }[]>(`/object/${location.state.id}/mean/segments`)
				.then(({ data }) => setOptionsMeans([{id:0,name:'Оборудование'},...data]));
		}
	}, [location.state]);

	const requestForInformList = () => {
		$api
			.get<string[]>(`/directory/dropdown/security-tool/segments`)
			.then(({ data }) => setOptionsSegment(data.map((_, index) => ({ id: index + 1, name: _ }))));
	};

	/**
	 * Стэйт модалки создания строки для таблицы
	 */
	const [createRow, setCreateRow] = useState(false);

	/**
	 * Стэйт для храниения и изменения названия организации
	 */
	const [activeName, setActiveName] = useState("");

	/**
	 * Стэйт таблиц и названия организации
	 */
	const [organization, setOrganization] = useState<IOrganization>({
		name: "",
		location: [],
		TTH: [],
		Soft: [],
		IL: [],
		MS: [],
	});

	const [optionsMeans, setOptionsMeans] = useState<{ id: number; name: string }[]>([]);
	const [optionsSegment, setOptionsSegment] = useState<{ id: number; name: string }[]>([]);
	console.log(organization)
	/**
	 * Замена таблицы
	 * @param obj - Вся измененная таблица
	 * @param name - Ключ таблицы в виде строки
	 */
	function replaceObj(obj: IData[] | ILocation[], name: string): void {
		setOrganization((prevState) => ({ ...prevState, [name]: obj }));
	}

	/**
	 * Добавление строки в таблицу
	 * @param key - Ключ таблицы
	 * @param obj - Строка которую нужно вставить
	 */
	function addRow(key: keyof IOrganization, obj: any) {
		if (key === "location") {
			requestForMeans();
		}
		if (key !== "name") {
			setOrganization((prevState) => ({ ...prevState, [key]: [...prevState[key]!, obj] }));
		}
	}

	/**
	 * Эффект запускается единожды при открытии компонента
	 * Запускает загрузку таблиц
	 */

	/**
	 * Эффект срабатывает при смене имени организации и записывает новое значение в стэйт
	 */
	useEffect(() => {
		setOrganization((prevState) => ({ ...prevState, name: activeName }));
	}, [activeName]);

	const { id, name } = location.state as ILocationState;

	useEffect(() => {
		if (id) {
			setActiveName(name);
			fetchTableData(id).then((r) => {
				setOrganization((prevState) => ({
					...prevState,
					...(r && { ...r }),
				}));
			});
		}
		//eslint-disable-next-line
	}, [id]);

	const headerRef = useRef<HTMLDivElement | null>(null);
	const fulled = useMemo(() => {
		const { name, ...rest } = organization;
		let flag = false;
		let restKey: keyof typeof rest;
		for (restKey in rest) {
			if (rest[restKey].length) {
				flag = true;
			} else {
				flag = false;
				break;
			}
		}
		return flag;
		//eslint-disable-next-line
	}, [organization]);

	const { stepId } = useParams();

	useEffect(() => {
		requestForMeans();
		requestForInformList();
		//eslint-disable-next-line
	}, [location.state]);

	return (
		<>
			{createRow && (
				<ModalPortal title={"Создать строку"} handleClose={() => setCreateRow(false)} outSide={false}>
					<CreateRow
						informOptions={optionsSegment}
						meansOptions={optionsMeans}
						add={addRow}
						close={() => setCreateRow(false)}
						id={id}
					/>
				</ModalPortal>
			)}
			<div className={sass.wrapper}>
				<main className={sass.page}>
					<div ref={headerRef} className={sass.sticky}>
						<HeadPage
							parentFuncForRedirect={() => {
								fulled && stepId === "2.2" && dispatch(ChangeStatus(id));
							}}
							title={"Доп. сведения об объекте"}
							topTitle={"2.2 Создание реестра объектов"}
							subTitle={organization.name}>
							<Button variant={BUTTON_VARIANTS.CONTAINED} className={sass.plus} onClick={() => setCreateRow(true)}>
								<AddIcon /> Добавить строку
							</Button>
						</HeadPage>
						{/*<HeaderTable nameOrganization={} temp={changeActiveName}*/}
						{/*             create={() => setCreateRow(true)}/>*/}
						<div data-comment-false={true} className={sass.tabs}>
							<div
								onClick={changeActiveTab}
								data-tab={1}
								className={cn(sass.tab, { [sass.tab_active]: activeTab === 1 })}>
								Расположение
							</div>
							<div
								onClick={changeActiveTab}
								data-tab={2}
								className={cn(sass.tab, { [sass.tab_active]: activeTab === 2 })}>
								Перечень основных технических средств и систем
							</div>
							<div
								onClick={changeActiveTab}
								data-tab={3}
								className={cn(sass.tab, { [sass.tab_active]: activeTab === 3 })}>
								Операционные системы
							</div>
							<div
								onClick={changeActiveTab}
								data-tab={4}
								className={cn(sass.tab, { [sass.tab_active]: activeTab === 4 })}>
								Программное обеспечение по ведению данных
							</div>
							<div
								onClick={changeActiveTab}
								data-tab={5}
								className={cn(sass.tab, { [sass.tab_active]: activeTab === 5 })}>
								Перечень средств защиты информации
							</div>
						</div>
					</div>
					{activeTab === 1 ? (
						<LocationVols
							direction={direction.direction}
							close={direction.exit}
							tab={1}
							activeTab={activeTab}
							deleteRow={(obj: ILocation[]) => replaceObj(obj, "location")}
							data={organization.location}
							nameOrganization={organization.name}
						/>
					) : activeTab === 2 ? (
						<TechListVols
							meansOptions={optionsMeans}
							direction={direction.direction}
							close={direction.exit}
							tab={2}
							objects={influenceObjects}
							activeTab={activeTab}
							deleteRow={(obj: IData[]) => replaceObj(obj, "TTH")}
							data={organization.TTH}
							nameOrganization={organization.name}
						/>
					) : activeTab === 3 ? (
						<OperatingSystem
							direction={direction.direction}
							close={direction.exit}
							tab={3}
							objects={influenceObjects}
							activeTab={activeTab}
							deleteRow={(obj: IData[]) => replaceObj(obj, "Soft")}
							data={organization.Soft}
							nameOrganization={organization.name}
						/>
					) : activeTab === 4 ? (
						<InformListVols
							direction={direction.direction}
							close={direction.exit}
							tab={4}
							objects={influenceObjects}
							activeTab={activeTab}
							deleteRow={(obj: IData[]) => replaceObj(obj, "IL")}
							data={organization.IL}
							nameOrganization={organization.name}
						/>
					) : (
						activeTab === 5 && (
							<ManagementSoftware
								options={optionsSegment}
								direction={direction.direction}
								close={direction.exit}
								objects={influenceObjects}
								tab={5}
								activeTab={activeTab}
								deleteRow={(obj: IData[]) => replaceObj(obj, "MS")}
								data={organization.MS}
							/>
						)
					)}
				</main>
			</div>
		</>
	);
};

export default StepTwoDotTwo;
